// Atlas Dashboard — Root Component
// Sets up routing and layout. Shows Login when not authenticated,
// shows the dashboard with all pages when authenticated.

import { useState, useEffect } from "react";
import {
  Eye, Mail, MessageCircle, Users, Megaphone, BarChart3,
  FileText, Search, Settings as SettingsIcon, Home, Activity, LogOut,
} from "lucide-react";
import { isAuthenticated, clearAuthToken } from "./lib/api-client";
import { Login } from "./pages/Login";
import { AuthCallback } from "./pages/AuthCallback";
import { EmailsPage } from "./pages/Emails";
import { ContactsPage } from "./pages/Contacts";
import { ConversationsPage } from "./pages/Conversations";
import { AnalyticsPage } from "./pages/Analytics";
import { SearchPage } from "./pages/Search";
import { SettingsPage } from "./pages/Settings";
import { ReportsPage } from "./pages/Reports";
import { useHome } from "./hooks/useApi";
import { formatIstDate, formatPercent } from "./lib/format";

const navItems = [
  { label: "Home", icon: Home, key: "home" },
  { label: "Tracked Emails", icon: Mail, key: "emails" },
  { label: "Conversations", icon: MessageCircle, key: "conversations" },
  { label: "Contacts", icon: Users, key: "contacts" },
  { label: "Analytics", icon: BarChart3, key: "analytics" },
  { label: "Reports", icon: FileText, key: "reports" },
  { label: "Search", icon: Search, key: "search" },
  { label: "Settings", icon: SettingsIcon, key: "settings" },
];

export function App() {
  const [authed, setAuthed] = useState(isAuthenticated());
  const [activeSection, setActiveSection] = useState("home");
  const [route, setRoute] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setRoute(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Handle OAuth callback route
  if (route === "/auth/success" || route === "/auth/callback") {
    return <AuthCallback />;
  }

  // Handle login route
  if (route === "/login" || !authed) {
    return <Login />;
  }

  function handleLogout() {
    clearAuthToken();
    setAuthed(false);
    window.location.href = "/login";
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", backgroundColor: "var(--color-background)" }}>
      {/* Sidebar */}
      <aside style={{ width: "240px", backgroundColor: "var(--color-sidebar)", color: "#E5E7EB", padding: "16px 0", display: "flex", flexDirection: "column", gap: "4px", flexShrink: 0 }}>
        <div style={{ padding: "0 24px 24px", borderBottom: "1px solid rgba(255,255,255,0.1)", marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Activity size={20} style={{ color: "#93C5FD" }} />
            <span style={{ fontSize: "18px", fontWeight: 700, color: "white" }}>Atlas</span>
          </div>
          <div style={{ fontSize: "12px", color: "#9CA3AF", marginTop: "4px" }}>Email Intelligence</div>
        </div>

        <nav style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "0 8px", flex: 1 }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setActiveSection(item.key)}
                style={{
                  display: "flex", alignItems: "center", gap: "12px",
                  padding: "10px 16px", borderRadius: "6px", border: "none",
                  background: isActive ? "rgba(37, 99, 235, 0.2)" : "transparent",
                  color: isActive ? "#93C5FD" : "#D1D5DB",
                  cursor: "pointer", fontSize: "14px", fontWeight: isActive ? 600 : 400,
                  textAlign: "left" as const, transition: "background 0.15s, color 0.15s",
                }}
              >
                <Icon size={16} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#9CA3AF", marginBottom: "12px" }}>
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#16A34A" }} />
            System healthy
          </div>
          <button onClick={handleLogout} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", borderRadius: "6px", border: "none", background: "transparent", color: "#9CA3AF", cursor: "pointer", fontSize: "13px", width: "100%", textAlign: "left" as const }}>
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, padding: "32px", overflowY: "auto" }}>
        <h1 style={{ fontSize: "24px", fontWeight: 700, color: "var(--color-text-primary)", margin: "0 0 24px" }}>
          {navItems.find((i) => i.key === activeSection)?.label}
        </h1>

        {activeSection === "home" && <HomeView />}
        {activeSection === "emails" && <EmailsPage />}
        {activeSection === "conversations" && <ConversationsPage />}
        {activeSection === "contacts" && <ContactsPage />}
        {activeSection === "analytics" && <AnalyticsPage />}
        {activeSection === "reports" && <ReportsPage />}
        {activeSection === "search" && <SearchPage />}
        {activeSection === "settings" && <SettingsPage />}
      </main>
    </div>
  );
}

// ─── Home view (connected to real API) ───

function HomeView() {
  const { data, loading, error } = useHome();

  if (loading) return <div style={{ padding: "48px", textAlign: "center", color: "var(--color-text-tertiary)" }}>Loading your dashboard...</div>;
  if (error) return <div style={{ padding: "48px", textAlign: "center", color: "#DC2626" }}>{error}</div>;
  if (!data) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
        <SummaryCard label="Sent Today" value={String(data.summary.sentToday)} />
        <SummaryCard label="Opened Today" value={String(data.summary.openedToday)} note={data.summary.limitationNote} />
        <SummaryCard label="Clicked Today" value={String(data.summary.clickedToday)} />
        <SummaryCard label="Replies Today" value={String(data.summary.repliesToday)} />
      </div>

      {/* What needs attention */}
      <div style={{ backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)", padding: "24px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: 600, margin: "0 0 16px" }}>What needs attention</h2>
        {data.attention.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {data.attention.map((item, i) => (
              <div key={i} style={{ padding: "10px 14px", backgroundColor: "#F9FAFB", borderRadius: "6px", fontSize: "14px", color: "var(--color-text-primary)" }}>
                {item.text}
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>{data.attentionEmpty}</p>
        )}
      </div>

      {/* System health */}
      <div style={{ backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)", padding: "24px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: 600, margin: "0 0 16px" }}>System health</h2>
        <div style={{ display: "flex", gap: "32px", flexWrap: "wrap" }}>
          <HealthIndicator label="Tracking" status={data.systemHealth.trackingEndpoint} />
          <HealthIndicator label="Gmail" status={data.systemHealth.gmailConnection} />
          <HealthIndicator label="Scheduled Jobs" status={data.systemHealth.scheduledJobs} />
          <HealthIndicator label="Storage" status="healthy" detail={`${Math.round((data.systemHealth.storageUsage.d1Percent + data.systemHealth.storageUsage.r2Percent) / 2)}% used`} />
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div style={{ backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)", padding: "16px" }}>
      <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: "8px" }}>{label}</div>
      <div style={{ fontSize: "32px", fontWeight: 700, color: "var(--color-text-primary)" }}>{value}</div>
      {note && <div style={{ fontSize: "12px", color: "var(--color-text-tertiary)", marginTop: "4px" }}>{note}</div>}
    </div>
  );
}

function HealthIndicator({ label, status, detail }: { label: string; status: string; detail?: string }) {
  const colors: Record<string, string> = { healthy: "#16A34A", warning: "#D97706", error: "#DC2626", connected: "#16A34A", expired: "#D97706", disconnected: "#6B7280" };
  const labels: Record<string, string> = { healthy: "Healthy", warning: "Warning", error: "Error", connected: "Connected", expired: "Reconnect needed", disconnected: "Not connected" };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: colors[status] || "#6B7280" }} />
      <div>
        <div style={{ fontSize: "14px", fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: "12px", color: "var(--color-text-tertiary)" }}>{labels[status] || status}{detail ? ` — ${detail}` : ""}</div>
      </div>
    </div>
  );
}
